import { OpenAPI } from "simplydo/interfaces";

import { API, APIError } from ".";
import SearchParams from "../searchParams";

export const boards = (api: API) => ({
  getProjectLanes(
    forId: string,
    success: (data: OpenAPI.GET<"/boards/{for_id}/lanes">["response"]) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest(
      "GET",
      `/boards/${forId}/lanes`,
      null,
      success,
      fail,
    );
  },
  importProjectLanes(
    forId: string,
    data: OpenAPI.POST<"/boards/{for_id}/lanes/import">["requestBody"],
    success: (
      data: OpenAPI.POST<"/boards/{for_id}/lanes/import">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest(
      "POST",
      `/boards/${forId}/lanes/import`,
      data,
      success,
      fail,
    );
  },
  createProjectLane(
    forId: string,
    data: OpenAPI.POST<"/boards/{for_id}/lanes">["requestBody"],
    success: (data: OpenAPI.POST<"/boards/{for_id}/lanes">["response"]) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest(
      "POST",
      `/boards/${forId}/lanes`,
      data,
      success,
      fail,
    );
  },
  updateProjectLane(
    forId: string,
    laneId: string,
    data: OpenAPI.PUT<"/boards/{for_id}/lanes/{lane_id}">["requestBody"],
    success: (
      data: OpenAPI.PUT<"/boards/{for_id}/lanes/{lane_id}">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest(
      "PUT",
      `/boards/${forId}/lanes/${laneId}`,
      data,
      success,
      fail,
    );
  },
  deleteProjectLane(
    forId: string,
    laneId: string,
    success: (
      data: OpenAPI.DELETE<"/boards/{for_id}/lanes/{lane_id}">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest(
      "DELETE",
      `/boards/${forId}/lanes/${laneId}`,
      null,
      success,
      fail,
    );
  },
  moveProjectIdeas(
    forId: string,
    data: {
      originalLane?: string;
      ideaIds?: string[];
      targetLane: string;
      order: number;
      sendAnnouncements?: unknown[];
    },
    success: (
      data: OpenAPI.PUT<"/boards/{for_id}/ideas/lanes">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest(
      "PUT",
      `/boards/${forId}/ideas/lanes`,
      data,
      success,
      fail,
    );
  },
  updateProjectIdeaDeadline(
    forId: string,
    ideaId: string,
    deadline: Date | null,
    success: (
      data: OpenAPI.PUT<"/boards/{for_id}/ideas/{idea_id}/deadline">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest(
      "PUT",
      `/boards/${forId}/ideas/${ideaId}/deadline`,
      { deadline },
      success,
      fail,
    );
  },
  updateProjectIdeasDeadline(
    forId: string,
    data: OpenAPI.PUT<"/boards/{for_id}/ideas/deadline">["requestBody"],
    success: (
      data: OpenAPI.PUT<"/boards/{for_id}/ideas/deadline">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest(
      "PUT",
      `/boards/${forId}/ideas/deadline`,
      data,
      success,
      fail,
    );
  },
  updateProjectIdeaReviewDate(
    forId: string,
    ideaId: string,
    reviewDate: Date | null,
    success: (
      data: OpenAPI.PUT<"/boards/{for_id}/ideas/{idea_id}/reviewDate">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest(
      "PUT",
      `/boards/${forId}/ideas/${ideaId}/reviewDate`,
      { reviewDate },
      success,
      fail,
    );
  },
  searchProjectAssignees(
    forId: string,
    query: string,
    success: (
      data: OpenAPI.GET<"/boards/{for_id}/assignees/search">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest(
      "GET",
      `/boards/${forId}/assignees/search?query=${query}`,
      null,
      success,
      fail,
    );
  },
  bulkAssignProjectIdeas(
    forId: string,
    data: OpenAPI.PUT<"/boards/{for_id}/ideas/assignees">["requestBody"],
    success: (
      data: OpenAPI.PUT<"/boards/{for_id}/ideas/assignees">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest(
      "PUT",
      `/boards/${forId}/ideas/assignees`,
      data,
      success,
      fail,
    );
  },
  assignProjectIdea(
    forId: string,
    ideaId: string,
    userId: string,
    data: OpenAPI.PUT<"/boards/{for_id}/ideas/{idea_id}/assignees/{user_id}">["requestBody"],
    success: (
      data: OpenAPI.PUT<"/boards/{for_id}/ideas/{idea_id}/assignees/{user_id}">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest(
      "PUT",
      `/boards/${forId}/ideas/${ideaId}/assignees/${userId}`,
      data,
      success,
      fail,
    );
  },
  unassignProjectIdea(
    forId: string,
    ideaId: string,
    userId: string,
    success: (
      data: OpenAPI.DELETE<"/boards/{for_id}/ideas/{idea_id}/assignees/{user_id}">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest(
      "DELETE",
      `/boards/${forId}/ideas/${ideaId}/assignees/${userId}`,
      null,
      success,
      fail,
    );
  },
  getProjectIdeaEvents(
    forId: string,
    ideaId: string,
    success: (data: OpenAPI.GET<"/boards/{for_id}/events">["response"]) => void,
    fail: (error: APIError) => void,
  ) {
    const queryParams = new SearchParams({
      idea_id: ideaId,
      page: 1,
      limit: 999,
    });
    api.authenticatedRequest(
      "GET",
      `/boards/${forId}/events?${queryParams.toSafeString()}`,
      null,
      success,
      fail,
    );
  },
  getProjectComments(
    forId: string,
    ideaId: string,
    params: OpenAPI.GET<"/boards/{for_id}/ideas/{idea_id}/comments">["parameters"]["query"],
    success: (
      data: OpenAPI.GET<"/boards/{for_id}/ideas/{idea_id}/comments">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    const queryParams = new SearchParams(params).toSafeString();
    const query = `/boards/${forId}/ideas/${ideaId}/comments${queryParams}`;
    api.authenticatedRequest("GET", query, null, success, fail);
  },
  postProjectComment(
    forId: string,
    ideaId: string,
    data: OpenAPI.POST<"/boards/{for_id}/ideas/{idea_id}/comments">["requestBody"],
    success: (
      data: OpenAPI.POST<"/boards/{for_id}/ideas/{idea_id}/comments">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest(
      "POST",
      `/boards/${forId}/ideas/${ideaId}/comments`,
      data,
      success,
      fail,
    );
  },
  deleteProjectComment(
    forId: string,
    ideaId: string,
    commentId: string,
    success: (
      data: OpenAPI.DELETE<"/boards/{for_id}/ideas/{idea_id}/comments/{comment_id}">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest(
      "DELETE",
      `/boards/${forId}/ideas/${ideaId}/comments/${commentId}`,
      null,
      success,
      fail,
    );
  },
  updateProjectComment(
    forId: string,
    ideaId: string,
    commentId: string,
    data: OpenAPI.POST<"/boards/{for_id}/ideas/{idea_id}/comments/{comment_id}">["requestBody"],
    success: (
      data: OpenAPI.POST<"/boards/{for_id}/ideas/{idea_id}/comments/{comment_id}">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest(
      "POST",
      `/boards/${forId}/ideas/${ideaId}/comments/${commentId}`,
      data,
      success,
      fail,
    );
  },
  getBoardEvents(
    forId: string,
    params: OpenAPI.GET<"/boards/{for_id}/events">["parameters"]["query"],
    success: (data: OpenAPI.GET<"/boards/{for_id}/events">["response"]) => void,
    fail: (error: APIError) => void,
  ) {
    const queryParams = new SearchParams(params);
    api.authenticatedRequest(
      "GET",
      `/boards/${forId}/events?${queryParams.toSafeString()}`,
      null,
      success,
      fail,
    );
  },
  markBoardEventsAsSeen(
    forId: string,
    success: (
      data: OpenAPI.POST<"/boards/{for_id}/events">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest(
      "POST",
      `/boards/${forId}/events`,
      null,
      success,
      fail,
    );
  },
  markBoardEventAsInteracted(
    forId: string,
    eventId: string,
    success: (
      data: OpenAPI.POST<"/boards/{for_id}/events/{event_id}">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest(
      "POST",
      `/boards/${forId}/events/${eventId}`,
      null,
      success,
      fail,
    );
  },
  reorderLaneIdeas(
    forId: string,
    laneId: string,
    sort: string,
    sortDirection: string,
    success: (
      data: OpenAPI.PUT<"/boards/{for_id}/lanes/{lane_id}/ideas/order">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest(
      "PUT",
      `/boards/${forId}/lanes/${laneId}/ideas/order?sort=${sort}&sortDirection=${sortDirection}`,
      null,
      success,
      fail,
    );
  },
});
